/**
 * @fileoverview API principal para formatação e parsing de dados
 * @module Fmt
 * @version 1.0.0
 *
 * @example
 * ```ts
 * import { fmt, parse } from "./fmt";
 *
 * // Formatação
 * fmt.dates.BR("20250528"); // "28/05/2025"
 * fmt.numbers.currency(1234.56); // "R$ 1.234,56"
 * fmt.strings.capitalize("joão silva"); // "João Silva"
 *
 * // Parsing
 * parse.dates.BR("28/05/2025"); // Date(2025-05-28)
 * parse.numbers.float("R$ 1.234,56"); // 1234.56
 * parse.protheus.error(errorString); // ProtheusError object
 * ```
 */

import { isValid } from "date-fns";
import {
  formatInternal,
  parseInternal,
  calculateDateDifference,
} from "./utils";
import type {
  FormattableValue,
  ParseableValue,
  DateFormatOptions,
  NumberFormatOptions,
  StringFormatOptions,
  NullableParseOptions,
  DateYYYYMMDDParseOptions,
  ProtheusError,
} from "./types";

/**
 * Namespace para formatação de dados
 * Contém funções especializadas para formatação de datas, números e strings
 *
 * @namespace
 * @public
 */
export const fmt = {
  /**
   * Funções para formatação de datas
   */
  dates: {
    /**
     * Formata valor para formato brasileiro de data (dd/MM/yyyy)
     * Suporta conversão automática de strings YYYYMMDD
     *
     * @param value - Valor a ser formatado (string | number | Date)
     * @param options - Opções de formatação
     * @returns Data formatada em dd/MM/yyyy ou fallback
     *
     * @example
     * ```ts
     * fmt.dates.BR("20250528"); // "28/05/2025"
     * fmt.dates.BR(new Date(2025, 4, 28)); // "28/05/2025"
     * fmt.dates.BR(null, { fallback: "N/A" }); // "N/A"
     * ```
     */
    BR: (value: FormattableValue, options: DateFormatOptions = {}): string => {
      return formatInternal(value, {
        category: "date:BR",
        fallback: options.fallback || "",
      });
    },

    /**
     * Formata valor para formato numérico de data (yyyyMMdd)
     *
     * @param value - Valor a ser formatado
     * @param options - Opções de formatação
     * @returns Data formatada em yyyyMMdd ou fallback
     *
     * @example
     * ```ts
     * fmt.dates.YYYYMMDD(new Date(2025, 4, 28)); // "20250528"
     * ```
     */
    YYYYMMDD: (
      value: FormattableValue,
      options: DateFormatOptions = {}
    ): string => {
      return formatInternal(value, {
        category: "date:YYYYMMDD",
        fallback: options.fallback || "",
      });
    },

    /**
     * Converte Date ou string ISO para formato yyyy-MM-dd
     *
     * @param value - Data a ser convertida
     * @param options - Opções de formatação
     * @returns Data no formato ISO (yyyy-MM-dd) ou fallback
     *
     * @example
     * ```ts
     * fmt.dates.ISO(new Date(2025, 4, 28)); // "2025-05-28"
     * fmt.dates.ISO("2025-05-28T10:30:00Z"); // "2025-05-28"
     * ```
     */
    ISO: (
      value: Date | string | null | undefined,
      options: DateFormatOptions = {}
    ): string => {
      if (!value) {
        return options.fallback ?? "";
      }

      let date: Date;
      if (typeof value === "string") {
        // Se for só a data (yyyy-MM-dd), parse como local
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [yyyy, mm, dd] = value.split("-").map(Number);
          date = new Date(yyyy, mm - 1, dd);
        } else {
          // Se vier com horário, pode ser UTC, então converte para local
          date = new Date(value);
        }
      } else {
        date = value;
      }

      return !Number.isNaN(date.getTime())
        ? [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, "0"),
            String(date.getDate()).padStart(2, "0"),
          ].join("-")
        : options.fallback ?? "";
    },

    /**
     * Calcula diferença em dias entre duas datas
     *
     * @param endDate - Data final
     * @param startDate - Data inicial (padrão: hoje)
     * @returns Diferença em dias (positivo se endDate > startDate)
     *
     * @example
     * ```ts
     * const future = new Date(2025, 11, 25);
     * const today = new Date(2025, 11, 20);
     * fmt.dates.differenceInCalendarDays(future, today); // 5
     * ```
     */
    differenceInCalendarDays: calculateDateDifference,

    /**
     * Normaliza string de data que contenha horário
     * Extrai apenas a parte da data (dd/MM/yyyy) ignorando horário
     *
     * @param value - String contendo data e possivelmente horário
     * @param options - Opções de formatação
     * @returns Data normalizada em dd/MM/yyyy ou fallback
     *
     * @example
     * ```ts
     * fmt.dates.normalizeDateString("11/10/2024 11:01:53"); // "11/10/2024"
     * fmt.dates.normalizeDateString("25/12/2024"); // "25/12/2024"
     * ```
     */
    normalizeDateString: (
      value: string | null | undefined,
      options: DateFormatOptions = {}
    ): string => {
      if (!value) {
        return options.fallback || "";
      }

      const dateMatch = value.match(/^\d{2}\/\d{2}\/\d{4}/);
      if (!dateMatch) {
        return options.fallback || "";
      }

      const dateString = dateMatch[0];
      const parsedDate = parse.dates.BR(dateString, { allowNull: true });

      return parsedDate && isValid(parsedDate)
        ? fmt.dates.BR(parsedDate, options)
        : options.fallback || "";
    },
  },

  /**
   * Funções para formatação de números
   */
  numbers: {
    /**
     * Formata valor numérico como moeda brasileira
     *
     * @param value - Valor a ser formatado
     * @param options - Opções de formatação
     * @returns Valor formatado como R$ X.XXX,XX ou fallback
     *
     * @example
     * ```ts
     * fmt.numbers.currency(1234.56); // "R$ 1.234,56"
     * fmt.numbers.currency(1000, { decimals: 0 }); // "R$ 1.000"
     * ```
     */
    currency: (
      value: FormattableValue,
      options: NumberFormatOptions = {}
    ): string => {
      return formatInternal(value, {
        category: "currency",
        decimals: options.decimals ?? 2,
        fallback: options.fallback || "",
      });
    },

    /**
     * Formata valor numérico como porcentagem
     *
     * @param value - Valor a ser formatado
     * @param options - Opções de formatação
     * @returns Valor formatado como X.XX% ou fallback
     *
     * @example
     * ```ts
     * fmt.numbers.percentage(12.345); // "12.35%"
     * fmt.numbers.percentage(50, { decimals: 0 }); // "50%"
     * ```
     */
    percentage: (
      value: FormattableValue,
      options: NumberFormatOptions = {}
    ): string => {
      return formatInternal(value, {
        category: "percentage",
        decimals: options.decimals ?? 2,
        fallback: options.fallback || "",
      });
    },
  },

  /**
   * Funções para formatação de strings
   */
  strings: {
    /**
     * Formata CNPJ aplicando máscara padrão
     *
     * @param value - CNPJ (apenas dígitos ou já formatado)
     * @param options - Opções de formatação
     * @returns CNPJ formatado como 00.000.000/0000-00 ou valor original
     *
     * @example
     * ```ts
     * fmt.strings.cnpj("12345678000195"); // "12.345.678/0001-95"
     * fmt.strings.cnpj("123456789"); // "123456789" (inválido, retorna original)
     * ```
     */
    cnpj: (
      value: FormattableValue,
      options: StringFormatOptions = {}
    ): string => {
      return formatInternal(value, {
        category: "cnpj",
        fallback: options.fallback || String(value || ""),
      });
    },

    /**
     * Capitaliza cada palavra na string
     *
     * @param value - String a ser capitalizada
     * @param options - Opções de formatação
     * @returns String com cada palavra capitalizada ou fallback
     *
     * @example
     * ```ts
     * fmt.strings.capitalize("joão_silva-dos.santos"); // "João Silva Dos Santos"
     * fmt.strings.capitalize("MARIA JOSÉ"); // "Maria José"
     * ```
     */
    capitalize: (
      value: string | null | undefined,
      options: StringFormatOptions = {}
    ): string => {
      return formatInternal(value, {
        category: "capitalize",
        fallback: options.fallback || "",
      });
    },

    /**
     * Gera iniciais a partir de uma string
     *
     * @param value - String para extrair iniciais
     * @param options - Opções de formatação
     * @returns Iniciais em maiúsculas ou fallback
     *
     * @example
     * ```ts
     * fmt.strings.initials("João Silva"); // "JS"
     * fmt.strings.initials("Maria José da Silva", { maxInitials: 3 }); // "MJS"
     * ```
     */
    initials: (
      value: string | null | undefined,
      options: StringFormatOptions = {}
    ): string => {
      return formatInternal(value, {
        category: "initials",
        maxInitials: options.maxInitials ?? 2,
        fallback: options.fallback || "",
      });
    },
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Funções de parsing com sobrecargas (definidas separadamente)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse de data no formato brasileiro (dd/MM/yyyy)
 *
 * @param value - String de data a ser parseada
 * @param options - Opções de parsing
 * @returns Date object ou null baseado em allowNull
 *
 * @example
 * ```ts
 * parseDatesBR("28/05/2025"); // Date(2025-05-28T00:00:00.000Z)
 * parseDatesBR("28/05/2025 14:30:00"); // Date(2025-05-28T00:00:00.000Z)
 * parseDatesBR("invalid", { allowNull: true }); // null
 * ```
 */
function parseDatesBR(
  value: ParseableValue,
  options: { allowNull: false }
): Date;
function parseDatesBR(
  value: ParseableValue,
  options?: { allowNull?: true }
): Date | null;
function parseDatesBR(
  value: ParseableValue,
  options: { allowNull?: boolean } = {}
): Date | null {
  return parseInternal(value, {
    category: "date:BR",
    allowNull: options.allowNull ?? false,
  }) as Date | null;
}

/**
 * Parse de data no formato YYYYMMDD
 *
 * @param value - String de data a ser parseada
 * @param options - Opções de parsing incluindo timePart opcional
 * @returns Date object ou null baseado em allowNull
 *
 * @example
 * ```ts
 * parseDatesYYYYMMDD("20250528"); // Date(2025-05-28T00:00:00.000Z)
 * parseDatesYYYYMMDD("20250528", { timePart: "143000" }); // Date com horário 14:30:00
 * ```
 */
function parseDatesYYYYMMDD(
  value: ParseableValue,
  options: { allowNull: false } & { timePart?: string }
): Date;
function parseDatesYYYYMMDD(
  value: ParseableValue,
  options?: DateYYYYMMDDParseOptions
): Date | null;
function parseDatesYYYYMMDD(
  value: ParseableValue,
  options: DateYYYYMMDDParseOptions = {}
): Date | null {
  return parseInternal(value, {
    category: "date:YYYYMMDD",
    timePart: options.timePart,
    allowNull: options.allowNull ?? false,
  }) as Date | null;
}

/**
 * Parse de número decimal com formato brasileiro
 * Remove símbolos de moeda e converte vírgulas para pontos
 *
 * @param value - String numérica a ser parseada
 * @param options - Opções de parsing
 * @returns Número decimal ou null baseado em allowNull
 *
 * @example
 * ```ts
 * parseNumbersFloat("R$ 1.234,56"); // 1234.56
 * parseNumbersFloat("1.234,56"); // 1234.56
 * parseNumbersFloat("1234.56"); // 1234.56
 * parseNumbersFloat("invalid", { allowNull: true }); // null
 * ```
 */
function parseNumbersFloat(
  value: ParseableValue,
  options: { allowNull: false }
): number;
function parseNumbersFloat(
  value: ParseableValue,
  options?: { allowNull?: true }
): number | null;
function parseNumbersFloat(
  value: ParseableValue,
  options: { allowNull?: boolean } = {}
): number | null {
  return parseInternal(value, {
    category: "float",
    allowNull: options.allowNull ?? false,
  }) as number | null;
}

/**
 * Extrai apenas dígitos de uma string e converte para inteiro
 *
 * @param value - String a ser processada
 * @param options - Opções de parsing
 * @returns Número inteiro ou null baseado em allowNull
 *
 * @example
 * ```ts
 * parseNumbersDigits("abc123def456"); // 123456
 * parseNumbersDigits("Tel: (11) 99999-9999"); // 1199999999
 * parseNumbersDigits("no digits", { allowNull: true }); // null
 * ```
 */
function parseNumbersDigits(
  value: ParseableValue,
  options: { allowNull: false }
): number;
function parseNumbersDigits(
  value: ParseableValue,
  options?: { allowNull?: true }
): number | null;
function parseNumbersDigits(
  value: ParseableValue,
  options: { allowNull?: boolean } = {}
): number | null {
  return parseInternal(value, {
    category: "digits",
    allowNull: options.allowNull ?? false,
  }) as number | null;
}

/**
 * Parse de strings de erro do sistema Protheus ERP
 * Extrai informações estruturadas de logs de erro
 *
 * @param value - String de erro do Protheus
 * @param options - Opções de parsing
 * @returns Objeto ProtheusError estruturado ou null
 *
 * @example
 * ```ts
 * const errorLog = "- AJUDACODE001 - cNFiscal 123456 - cSerie 1 - dDEmissao 28052025";
 * parseProtheusError(errorLog);
 * // {
 * //   code: "AJUDACODE001",
 * //   nf: "123456",
 * //   serie: "1",
 * //   fornecedor: undefined,
 * //   loja: undefined,
 * //   emissao: "28/05/2025"
 * // }
 * ```
 */
function parseProtheusError(
  value: ParseableValue,
  options: { allowNull: false }
): ProtheusError;
function parseProtheusError(
  value: ParseableValue,
  options?: { allowNull?: true }
): ProtheusError | null;
function parseProtheusError(
  value: ParseableValue,
  options: { allowNull?: boolean } = {}
): ProtheusError | null {
  return parseInternal(value, {
    category: "protheus:error",
    allowNull: options.allowNull ?? false,
  }) as ProtheusError | null;
}

/**
 * Parse de string ISO para Date
 * ✅ CORRIGIDO: Garante consistência evitando problemas de timezone
 *
 * @param value - String ISO a ser parseada
 * @param options - Opções de parsing
 * @returns Date object ou null
 *
 * @example
 * ```ts
 * parseDatesISO("2025-05-28T14:30:00Z"); // Date(2025-05-28T14:30:00.000Z)
 * parseDatesISO("2025-05-28"); // Date(2025-05-28T00:00:00) em timezone local
 * ```
 */
function parseDatesISO(
  value: string,
  options: NullableParseOptions = {}
): Date | null {
  if (!value?.trim()) {
    return options.allowNull ? null : new Date();
  }

  // ✅ CORRIGIDO: Se for só data (yyyy-MM-dd), parse como local para evitar problemas de timezone
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [yyyy, mm, dd] = value.trim().split("-").map(Number);
    if (yyyy && mm && dd && !isNaN(yyyy) && !isNaN(mm) && !isNaN(dd)) {
      return new Date(yyyy, mm - 1, dd);
    }
  }

  // Para strings com horário, usa construtor padrão
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return options.allowNull ? null : new Date();
  }
  return date;
}

// ─────────────────────────────────────────────────────────────────────────────
// Namespace de parsing (usando as funções definidas acima)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Namespace para parsing de dados
 * Contém funções especializadas para conversão de strings em tipos específicos
 *
 * @namespace
 * @public
 */
export const parse = {
  /**
   * Funções para parsing de datas
   */
  dates: {
    BR: parseDatesBR,
    ISO: parseDatesISO,
    YYYYMMDD: parseDatesYYYYMMDD,
  },

  /**
   * Funções para parsing de números
   */
  numbers: {
    float: parseNumbersFloat,
    digits: parseNumbersDigits,
  },

  /**
   * Funções para parsing específico do sistema Protheus
   */
  protheus: {
    error: parseProtheusError,
  },
} as const;
